import type { QueryClient } from "@tanstack/react-query";
import { accountKeys } from "@/features/accounts/hooks/query-keys";
import { financeDashboardKeys } from "@/features/finance/hooks/query-keys";
import { purchaseOrderKeys } from "@/features/purchase-orders/hooks/query-keys";
import { paymentKeys } from "@/features/vendor-payments/hooks/query-keys";
import { productKeys } from "@/features/inventory/hooks/query-keys";
import { poKanbanKeys } from "@/features/purchase-orders/hooks/query-keys";

/**
 * Refreshes every surface that reads a purchase order a write moved.
 *
 * A line write moves the order's own totals and counters, the product
 * collection the line belongs to, the kanban board's purchase-order projection
 * (a disjoint `poKanbanKeys` cache root), and the finance dashboard's payables
 * aging — none of which prefix-match each other.
 *
 * A header write reaches the same product and kanban trees for a non-obvious
 * reason: restating `account_id` or `date` regenerates the order's `uid`
 * (`xan_trg_generate_uid_gl_purchase_orders`), and
 * `update_products_on_po_change_trigger` then rewrites
 * `gl_products.purchase_order_uid`. Without this the product caches and the
 * kanban cards keep rendering a UID that no longer exists.
 *
 * The detail key is only invalidated when a caller names an order; a write with
 * no single owning order still moves the list, product, kanban, and dashboard
 * trees. Only a landed write reaches here, so a rejected one leaves every cache
 * as the operator left it.
 *
 * `accountKeys.all` is included because the vendor's account page lists its
 * purchase orders and their totals under `accountKeys.detail`.
 */
export async function reconcileProductWrite(
  queryClient: QueryClient,
  purchaseOrderId?: string,
): Promise<void> {
  await Promise.all([
    ...(purchaseOrderId
      ? [queryClient.invalidateQueries({ queryKey: purchaseOrderKeys.detail(purchaseOrderId) })]
      : []),
    queryClient.invalidateQueries({ queryKey: purchaseOrderKeys.all }),
    queryClient.invalidateQueries({ queryKey: productKeys.all }),
    queryClient.invalidateQueries({ queryKey: poKanbanKeys.purchaseOrders.all() }),
    queryClient.invalidateQueries({ queryKey: financeDashboardKeys.all }),
    queryClient.invalidateQueries({ queryKey: accountKeys.all }),
  ]);
}

/**
 * The line-write set plus the vendor-payment tree, for a header write.
 *
 * A separate reconciler rather than a flag on the one above, because the extra
 * tree is owned by a different trigger: `update_vendor_payments_on_po_change`
 * rewrites `gl_vendor_payments.account_id` and `date_of_purchase_order` when the
 * order's vendor or date changes, and every vendor-payment read embeds the
 * order's `uid` alongside them. A line write moves none of that, so folding the
 * two would over-invalidate the far more frequent path.
 */
export async function reconcilePurchaseOrderHeaderWrite(
  queryClient: QueryClient,
  purchaseOrderId: string,
): Promise<void> {
  await Promise.all([
    reconcileProductWrite(queryClient, purchaseOrderId),
    queryClient.invalidateQueries({ queryKey: paymentKeys.vendor.all }),
  ]);
}
